import { supabaseClient } from '../../utils/supabase';

export const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive';
export const DRIVE_FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder';

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  webViewLink?: string;
  iconLink?: string;
  modifiedTime?: string;
  size?: string;
}

export interface DriveListResult {
  files: DriveFile[];
  complete: boolean;
}

export type GoogleDriveErrorCode = 'auth' | 'access' | 'not_found' | 'too_large' | 'other';

export class GoogleDriveError extends Error {
  constructor(
    message: string,
    readonly code: GoogleDriveErrorCode,
    readonly status: number,
  ) {
    super(message);
    this.name = 'GoogleDriveError';
  }
}

export function extractDriveFolderId(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^[a-zA-Z0-9_-]{20,}$/.test(trimmed)) return trimmed;

  try {
    const url = new URL(trimmed);
    if (!['http:', 'https:'].includes(url.protocol) || url.hostname.toLowerCase() !== 'drive.google.com') {
      return null;
    }
    const foldersMatch = url.pathname.match(/\/folders\/([a-zA-Z0-9_-]+)/);
    if (foldersMatch?.[1]) return foldersMatch[1];
    const idParam = url.searchParams.get('id');
    if (idParam && /^[a-zA-Z0-9_-]+$/.test(idParam)) return idParam;
  } catch {
    return null;
  }

  return null;
}

/**
 * Drive links are stored in several legacy forms. Keep a single canonical URL
 * for user-facing actions so copy/open work without requiring a Google token.
 */
export function canonicalDriveFolderUrl(value: string | null | undefined): string | null {
  const folderId = extractDriveFolderId(value);
  return folderId ? `https://drive.google.com/drive/folders/${folderId}` : null;
}

export function formatDriveDate(value: string | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(value));
}

export function drivePreviewUrl(file: DriveFile): string {
  if (file.mimeType === DRIVE_FOLDER_MIME_TYPE) return '';
  return `https://drive.google.com/file/d/${file.id}/preview`;
}

function driveApiError(response: Response, action: string): Error {
  if (response.status === 401) {
    return new GoogleDriveError(
      'Google Drive permission expired. Reconnect Google Drive to continue.',
      'auth',
      response.status,
    );
  }
  if (response.status === 403) {
    return new GoogleDriveError(
      'Google Drive access denied for this Google account. Reconnect with an account that has access or open the folder in Drive.',
      'access',
      response.status,
    );
  }
  if (response.status === 404) {
    return new GoogleDriveError(
      'The linked Google Drive folder is not accessible to this Google account. Reconnect with an account that has access.',
      'not_found',
      response.status,
    );
  }
  return new GoogleDriveError(
    `Could not ${action} Google Drive files (${response.status}).`,
    'other',
    response.status,
  );
}

/**
 * Verify the parent itself before listing children. Drive files.list only
 * returns items visible to the token, so an inaccessible parent otherwise
 * looks exactly like a genuinely empty folder.
 */
async function assertDriveFolderAccessible(folderId: string, providerToken: string): Promise<void> {
  const params = new URLSearchParams({
    fields: 'id,mimeType',
    supportsAllDrives: 'true',
  });
  const response = await fetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(folderId)}?${params}`, {
    headers: { Authorization: `Bearer ${providerToken}` },
  });
  if (!response.ok) throw driveApiError(response, 'open');

  const folder = (await response.json()) as { mimeType?: string };
  if (folder.mimeType !== DRIVE_FOLDER_MIME_TYPE) {
    throw new GoogleDriveError(
      'The saved Google Drive link does not point to a folder.',
      'not_found',
      400,
    );
  }
}

export async function listDriveFilesComplete(
  folderId: string,
  providerToken: string,
  options: { maxItems?: number; verifyFolder?: boolean } = {},
): Promise<DriveListResult> {
  if (options.verifyFolder !== false) {
    await assertDriveFolderAccessible(folderId, providerToken);
  }

  const maxItems = options.maxItems ?? 5000;
  const files: DriveFile[] = [];
  let pageToken: string | undefined;

  do {
    const params = new URLSearchParams({
      q: `'${folderId}' in parents and trashed = false`,
      pageSize: '200',
      fields: 'nextPageToken,files(id,name,mimeType,webViewLink,iconLink,modifiedTime,size)',
      orderBy: 'folder,name',
      supportsAllDrives: 'true',
      includeItemsFromAllDrives: 'true',
    });
    if (pageToken) params.set('pageToken', pageToken);

    const response = await fetch(`https://www.googleapis.com/drive/v3/files?${params.toString()}`, {
      headers: { Authorization: `Bearer ${providerToken}` },
    });

    if (!response.ok) {
      throw driveApiError(response, 'load');
    }

    const payload = (await response.json()) as { files?: DriveFile[]; nextPageToken?: string };
    files.push(...(payload.files ?? []));
    pageToken = payload.nextPageToken;

    if (files.length >= maxItems && pageToken) {
      return { files: [], complete: false };
    }
  } while (pageToken);

  return { files, complete: true };
}

export async function listDriveFiles(folderId: string, providerToken: string): Promise<DriveFile[]> {
  const result = await listDriveFilesComplete(folderId, providerToken);
  if (!result.complete) {
    throw new Error('This Drive folder has too many files to display without narrowing the folder.');
  }
  return result.files;
}

export async function uploadDriveFile(
  folderId: string,
  file: File,
  providerToken: string,
): Promise<DriveFile> {
  return createDriveFile({ name: file.name, parents: [folderId] }, file, providerToken);
}

/**
 * Copies a spreadsheet into the user's own Drive as a new Google Sheet. Drive
 * converts CSV and XLSX on upload when the requested type is a Google Sheet.
 */
export async function createGoogleSheetCopy(name: string, file: Blob, providerToken: string): Promise<DriveFile> {
  return createDriveFile({ name: name.replace(/\.(xlsx|csv)$/i, ''), mimeType: GOOGLE_SHEET_MIME_TYPE }, file, providerToken);
}

async function createDriveFile(
  metadata: { name: string; parents?: string[]; mimeType?: string },
  file: Blob,
  providerToken: string,
): Promise<DriveFile> {
  const boundary = `skyline_drive_upload_${Date.now()}`;
  const body = new Blob([
    `--${boundary}\r\n`,
    'Content-Type: application/json; charset=UTF-8\r\n\r\n',
    JSON.stringify(metadata),
    '\r\n',
    `--${boundary}\r\n`,
    `Content-Type: ${file.type || 'application/octet-stream'}\r\n\r\n`,
    file,
    '\r\n',
    `--${boundary}--`,
  ]);
  const params = new URLSearchParams({
    uploadType: 'multipart',
    supportsAllDrives: 'true',
    fields: 'id,name,mimeType,webViewLink,iconLink,modifiedTime',
  });

  const response = await fetch(`https://www.googleapis.com/upload/drive/v3/files?${params.toString()}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${providerToken}`,
      'Content-Type': `multipart/related; boundary=${boundary}`,
    },
    body,
  });

  if (!response.ok) {
    throw driveApiError(response, 'upload to');
  }

  return (await response.json()) as DriveFile;
}

export async function getMerchantDriveFolder(merchantId: number): Promise<string | null> {
  const { data, error } = await supabaseClient
    .from('merchant')
    .select('drive_folder')
    .eq('id', merchantId)
    .maybeSingle();
  if (error) throw error;
  return data?.drive_folder ?? null;
}

async function getSubmissionMerchantId(submissionId: number): Promise<number | null> {
  const { data, error } = await supabaseClient
    .from('submission')
    .select('merchant')
    .eq('id', submissionId)
    .maybeSingle();
  if (error) throw error;
  return data?.merchant ?? null;
}

export async function getDriveFolderForRecord(
  tableName: string,
  recordId: number,
): Promise<string | null> {
  if (tableName === 'merchant') return getMerchantDriveFolder(recordId);
  if (tableName === 'submission') {
    const merchantId = await getSubmissionMerchantId(recordId);
    return merchantId != null ? getMerchantDriveFolder(merchantId) : null;
  }
  return null;
}

/* ── Spreadsheets (lead import snapshots) ─────────────── */

export const GOOGLE_SHEET_MIME_TYPE = 'application/vnd.google-apps.spreadsheet';
export const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const SPREADSHEET_MIME_TYPES = [GOOGLE_SHEET_MIME_TYPE, XLSX_MIME_TYPE, 'text/csv'];
/** Legacy Excel 97-2003. Imports accept .xlsx only; the file must be re-saved. */
const LEGACY_XLS_MIME_TYPE = 'application/vnd.ms-excel';
export const LEGACY_XLS_MESSAGE = 'Old Excel files (.xls) are not supported. Open the file in Excel or Google Sheets, save it as Excel Workbook (.xlsx), and upload that.';

export interface DriveSpreadsheetMeta extends DriveFile {
  version?: string;
  headRevisionId?: string;
  owners?: { displayName?: string; emailAddress?: string }[];
}

/** Spreadsheet id from a Sheets/Drive URL or a bare id. */
export function extractDriveFileId(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^[a-zA-Z0-9_-]{25,}$/.test(trimmed)) return trimmed;
  try {
    const url = new URL(trimmed);
    if (!/(^|\.)google\.com$/.test(url.hostname.toLowerCase())) return null;
    const m = url.pathname.match(/\/(?:spreadsheets\/)?d\/([a-zA-Z0-9_-]+)/);
    if (m?.[1]) return m[1];
    const idParam = url.searchParams.get('id');
    if (idParam && /^[a-zA-Z0-9_-]+$/.test(idParam)) return idParam;
  } catch {
    return null;
  }
  return null;
}

/**
 * Spreadsheets and folders visible to the token. With a query, matches file
 * names across Drive (including shared drives); without one, lists the most
 * recently modified spreadsheets, or the children of `folderId`.
 */
export async function searchDriveSpreadsheets(
  providerToken: string,
  options: { query?: string; folderId?: string } = {},
): Promise<DriveFile[]> {
  const mimeClause = SPREADSHEET_MIME_TYPES.map((m) => `mimeType = '${m}'`).join(' or ');
  const clauses = ['trashed = false'];
  if (options.folderId) {
    clauses.push(`'${options.folderId.replace(/'/g, '')}' in parents`);
    clauses.push(`(${mimeClause} or mimeType = '${DRIVE_FOLDER_MIME_TYPE}')`);
  } else {
    clauses.push(`(${mimeClause})`);
  }
  if (options.query?.trim()) clauses.push(`name contains '${options.query.trim().replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`);
  const params = new URLSearchParams({
    q: clauses.join(' and '),
    pageSize: '50',
    fields: 'files(id,name,mimeType,webViewLink,iconLink,modifiedTime,size)',
    orderBy: options.folderId ? 'folder,name' : 'modifiedTime desc',
    supportsAllDrives: 'true',
    includeItemsFromAllDrives: 'true',
  });
  const response = await fetch(`https://www.googleapis.com/drive/v3/files?${params}`, { headers: { Authorization: `Bearer ${providerToken}` } });
  if (!response.ok) throw driveApiError(response, 'search');
  return ((await response.json()) as { files?: DriveFile[] }).files ?? [];
}

export async function getDriveSpreadsheetMeta(fileId: string, providerToken: string): Promise<DriveSpreadsheetMeta> {
  const params = new URLSearchParams({
    fields: 'id,name,mimeType,webViewLink,modifiedTime,size,version,headRevisionId,owners(displayName,emailAddress)',
    supportsAllDrives: 'true',
  });
  const response = await fetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?${params}`, {
    headers: { Authorization: `Bearer ${providerToken}` },
  });
  if (!response.ok) throw driveApiError(response, 'open');
  const meta = (await response.json()) as DriveSpreadsheetMeta;
  if (meta.mimeType === LEGACY_XLS_MIME_TYPE) throw new GoogleDriveError(LEGACY_XLS_MESSAGE, 'other', 400);
  if (!SPREADSHEET_MIME_TYPES.includes(meta.mimeType)) {
    throw new GoogleDriveError('That Drive file is not a spreadsheet or CSV.', 'not_found', 400);
  }
  return meta;
}

/**
 * Drive answers export problems with 403 plus a reason. Export size is not an
 * access problem: the operator has the file, Drive just will not convert it,
 * so it gets its own code and a next step instead of "reconnect".
 */
async function driveDownloadError(response: Response): Promise<Error> {
  let reason = '';
  try {
    const body = await response.clone().json();
    reason = body?.error?.errors?.[0]?.reason ?? body?.error?.status ?? '';
  } catch {
    /* non-JSON error body */
  }
  if (reason === 'exportSizeLimitExceeded') {
    return new GoogleDriveError(
      'This spreadsheet is too large for Google Drive to export directly.',
      'too_large',
      response.status,
    );
  }
  if (response.status === 403 && /cannotDownload|cannotExport/i.test(reason)) {
    return new GoogleDriveError(
      'The owner of this spreadsheet does not allow downloading or copying it.',
      'access',
      response.status,
    );
  }
  return driveApiError(response, 'download');
}

/**
 * Download a spreadsheet as a file: Google Sheets are exported as XLSX (all
 * tabs, cached formula values), native Excel/CSV files are downloaded as-is.
 * Note Drive's export limit (about 10 MB of exported content).
 */
export async function downloadDriveSpreadsheet(meta: DriveSpreadsheetMeta, providerToken: string): Promise<{ file: File; exportedAs: string }> {
  const isSheet = meta.mimeType === GOOGLE_SHEET_MIME_TYPE;
  const url = isSheet
    ? `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(meta.id)}/export?mimeType=${encodeURIComponent(XLSX_MIME_TYPE)}`
    : `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(meta.id)}?alt=media&supportsAllDrives=true`;
  const response = await fetch(url, { headers: { Authorization: `Bearer ${providerToken}` } });
  if (!response.ok) throw await driveDownloadError(response);
  const blob = await response.blob();
  const exportedAs = isSheet ? XLSX_MIME_TYPE : meta.mimeType;
  const name = isSheet && !/\.xlsx$/i.test(meta.name) ? `${meta.name}.xlsx` : meta.name;
  return { file: new File([blob], name, { type: exportedAs }), exportedAs };
}
